import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Youtube, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAnalytics } from "@/hooks/use-analytics";

const FEATURED_VIDEO_ID = "HBH_HNFhmis";
const FEATURED_VIDEO_URL = `https://youtu.be/${FEATURED_VIDEO_ID}`;
const FALLBACK_TITLE = "The Federal Data Behind Hawaiʻi's Provider Shortage";
const FALLBACK_AUTHOR = "ISEI Insights";
const FALLBACK_THUMBNAIL = `https://i.ytimg.com/vi/${FEATURED_VIDEO_ID}/hqdefault.jpg`;
const HIGHRES_THUMBNAIL = `https://i.ytimg.com/vi/${FEATURED_VIDEO_ID}/maxresdefault.jpg`;

const API_LOAD_TIMEOUT_MS = 8000;

interface OEmbedData {
  title: string;
  author_name: string;
  thumbnail_url: string;
}

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let ytApiPromise: Promise<any> | null = null;
function loadYouTubeApi(): Promise<any> {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.onerror = () => {
      ytApiPromise = null;
      reject(new Error("script-load-failed"));
    };
    document.head.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT);
    };
    window.setTimeout(() => {
      if (!window.YT || !window.YT.Player) {
        ytApiPromise = null;
        reject(new Error("timeout"));
      }
    }, API_LOAD_TIMEOUT_MS);
  });
  return ytApiPromise;
}

function isMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export function MediaSection() {
  const { trackEvent } = useAnalytics();
  const [meta, setMeta] = useState<OEmbedData>({
    title: FALLBACK_TITLE,
    author_name: FALLBACK_AUTHOR,
    thumbnail_url: HIGHRES_THUMBNAIL,
  });
  const [activated, setActivated] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const playerRef = useRef<any>(null);
  const mountIdRef = useRef(`yt-player-${Math.random().toString(36).slice(2, 10)}`);
  const progressTimerRef = useRef<number | null>(null);
  const firedRef = useRef<{ p25: boolean; p50: boolean; p75: boolean; complete: boolean }>({
    p25: false,
    p50: false,
    p75: false,
    complete: false,
  });

  // --- Metadata fetch (cached oEmbed) ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      if (!projectId) return;
      try {
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/get-video-oembed?video_id=${FEATURED_VIDEO_ID}`,
        );
        if (!res.ok) return;
        const payload = await res.json();
        if (cancelled || !payload?.title) return;
        setMeta((prev) => ({
          title: payload.title || prev.title,
          author_name: payload.author_name || prev.author_name,
          thumbnail_url: payload.thumbnail_url || prev.thumbnail_url,
        }));
      } catch {
        /* keep fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stopProgressTracking = useCallback(() => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const startProgressTracking = useCallback(() => {
    stopProgressTracking();
    progressTimerRef.current = window.setInterval(() => {
      const player = playerRef.current;
      if (!player || typeof player.getDuration !== "function") return;
      const duration = player.getDuration();
      const current = player.getCurrentTime();
      if (!duration || duration < 1) return;
      const pct = (current / duration) * 100;
      const base = {
        location: "home_media_section",
        video_id: FEATURED_VIDEO_ID,
        title: meta.title,
      };
      if (!firedRef.current.p25 && pct >= 25) {
        firedRef.current.p25 = true;
        trackEvent("featured_video_progress_25", { ...base, percent: 25 });
      }
      if (!firedRef.current.p50 && pct >= 50) {
        firedRef.current.p50 = true;
        trackEvent("featured_video_progress_50", { ...base, percent: 50 });
      }
      if (!firedRef.current.p75 && pct >= 75) {
        firedRef.current.p75 = true;
        trackEvent("featured_video_progress_75", { ...base, percent: 75 });
      }
    }, 1000);
  }, [meta.title, trackEvent, stopProgressTracking]);

  // Hard reset of player + timers
  const teardownPlayer = useCallback(() => {
    stopProgressTracking();
    try {
      playerRef.current?.stopVideo?.();
    } catch { /* noop */ }
    try {
      playerRef.current?.destroy?.();
    } catch { /* noop */ }
    playerRef.current = null;
    setPlayerReady(false);
  }, [stopProgressTracking]);

  // --- Player init (re-runs on retryNonce change) ---
  useEffect(() => {
    if (!activated) return;
    let destroyed = false;
    setLoadError(null);
    setPlayerReady(false);

    loadYouTubeApi()
      .then((YT) => {
        if (destroyed) return;
        const mountEl = document.getElementById(mountIdRef.current);
        if (!mountEl) {
          setLoadError("mount-missing");
          return;
        }
        try {
          const mobile = isMobile();
          playerRef.current = new YT.Player(mountIdRef.current, {
            videoId: FEATURED_VIDEO_ID,
            width: "100%",
            height: "100%",
            playerVars: {
              rel: 0,
              modestbranding: 1,
              autoplay: 1,
              playsinline: 1, // critical for iOS inline playback
              // Mobile browsers block autoplay with sound; start muted so playback begins,
              // user can unmute via the YT chrome.
              mute: mobile ? 1 : 0,
            },
            events: {
              onReady: (event: any) => {
                if (destroyed) return;
                setPlayerReady(true);
                trackEvent("featured_video_ready", {
                  location: "home_media_section",
                  video_id: FEATURED_VIDEO_ID,
                  title: meta.title,
                });
                // Best-effort autoplay kick (iOS / Android Chrome sometimes need it)
                try {
                  event?.target?.playVideo?.();
                } catch { /* noop */ }
              },
              onError: (event: any) => {
                trackEvent("featured_video_error", {
                  location: "home_media_section",
                  video_id: FEATURED_VIDEO_ID,
                  error_code: String(event?.data ?? "unknown"),
                });
                setLoadError(`embed-error-${event?.data ?? "unknown"}`);
              },
              onStateChange: (event: any) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  trackEvent("featured_video_play_start", {
                    location: "home_media_section",
                    video_id: FEATURED_VIDEO_ID,
                    title: meta.title,
                  });
                  startProgressTracking();
                } else if (event.data === YT.PlayerState.PAUSED) {
                  stopProgressTracking();
                } else if (event.data === YT.PlayerState.ENDED) {
                  stopProgressTracking();
                  if (!firedRef.current.complete) {
                    firedRef.current.complete = true;
                    trackEvent("featured_video_play_complete", {
                      location: "home_media_section",
                      video_id: FEATURED_VIDEO_ID,
                      title: meta.title,
                      percent: 100,
                    });
                  }
                }
              },
            },
          });
        } catch (err) {
          setLoadError("player-init-failed");
          trackEvent("featured_video_error", {
            location: "home_media_section",
            video_id: FEATURED_VIDEO_ID,
            error_code: "player-init-failed",
          });
        }
      })
      .catch((err) => {
        if (destroyed) return;
        setLoadError(err?.message || "api-load-failed");
        trackEvent("featured_video_error", {
          location: "home_media_section",
          video_id: FEATURED_VIDEO_ID,
          error_code: err?.message || "api-load-failed",
        });
      });

    return () => {
      destroyed = true;
      teardownPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activated, retryNonce]);

  const activate = () => {
    if (activated) return;
    trackEvent("featured_video_thumbnail_click", {
      location: "home_media_section",
      video_id: FEATURED_VIDEO_ID,
      title: meta.title,
    });
    setActivated(true);
  };

  const retry = () => {
    trackEvent("featured_video_retry", {
      location: "home_media_section",
      video_id: FEATURED_VIDEO_ID,
    });
    // Reset everything before re-init
    teardownPlayer();
    firedRef.current = { p25: false, p50: false, p75: false, complete: false };
    ytApiPromise = null;
    setLoadError(null);
    setActivated(false);
    // Use a new mount id so React can re-render a fresh node
    mountIdRef.current = `yt-player-${Math.random().toString(36).slice(2, 10)}`;
    requestAnimationFrame(() => {
      setActivated(true);
      setRetryNonce((n) => n + 1);
    });
  };

  return (
    <section className="py-20 lg:py-28 border-t border-border/30">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-medium mb-8">
            Public Voice
          </p>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-5 leading-tight">
            Shaping the conversation
          </h2>
          <p className="text-[17px] text-muted-foreground leading-[1.75] mb-10">
            Engaging policymakers, media, and the public on the structural forces reshaping Hawaiʻi's healthcare system. Direct, evidence-backed, on the record.
          </p>

          <figure className="mb-6">
            <div className="relative w-full overflow-hidden rounded-md border border-border/40 bg-muted shadow-2xl aspect-video">
              {/* THUMBNAIL */}
              {!activated && !loadError && (
                <button
                  type="button"
                  onClick={activate}
                  aria-label={`Play video: ${meta.title}`}
                  // touch-action: manipulation removes the 300ms tap delay & prevents scroll-jank when tapping
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
                  className="group absolute inset-0 h-full w-full cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <img
                    src={meta.thumbnail_url}
                    srcSet={`${FALLBACK_THUMBNAIL} 480w, ${HIGHRES_THUMBNAIL} 1280w`}
                    sizes="(max-width: 640px) 100vw, 672px"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    width={1280}
                    height={720}
                    draggable={false}
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    onError={(e) => {
                      const img = e.currentTarget as HTMLImageElement;
                      if (!img.src.endsWith("/hqdefault.jpg")) img.src = FALLBACK_THUMBNAIL;
                    }}
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent/95 text-accent-foreground shadow-lg transition-transform duration-300 group-hover:scale-110">
                      <Play className="h-7 w-7 translate-x-0.5" fill="currentColor" />
                    </span>
                  </span>
                </button>
              )}

              {/* PLAYER + LOADING SKELETON */}
              {activated && !loadError && (
                <>
                  <div className="absolute inset-0 h-full w-full">
                    <div id={mountIdRef.current} className="h-full w-full" />
                  </div>
                  {!playerReady && (
                    <div
                      aria-live="polite"
                      className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted"
                    >
                      {/* Shimmer */}
                      <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-muted via-muted/70 to-muted" />
                      <div className="relative flex flex-col items-center gap-2">
                        <Loader2 className="h-7 w-7 animate-spin text-accent" />
                        <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                          Loading video…
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ERROR FALLBACK */}
              {loadError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center bg-muted">
                  <AlertTriangle className="h-8 w-8 text-accent" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-foreground">
                      We couldn't load the video player
                    </p>
                    <p className="text-xs text-muted-foreground max-w-sm">
                      The YouTube player may be blocked by your network or an extension. You can retry, or watch directly on YouTube.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button onClick={retry} size="sm" variant="default" className="h-9 gap-1.5">
                      <RefreshCw className="h-3.5 w-3.5" /> Retry
                    </Button>
                    <Button asChild size="sm" variant="outline" className="h-9 gap-1.5">
                      <a
                        href={FEATURED_VIDEO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          trackEvent("featured_video_external_click", {
                            location: "home_media_section",
                            video_id: FEATURED_VIDEO_ID,
                            destination: "youtube",
                            from: "fallback",
                          })
                        }
                      >
                        <Youtube className="h-3.5 w-3.5" /> Watch on YouTube
                      </a>
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <figcaption className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <span className="text-[11px] uppercase tracking-[0.2em] text-accent font-semibold">
                ▶ Featured Video · {meta.author_name}
              </span>
              <span className="text-[13px] sm:text-right font-medium text-foreground/80 leading-snug break-words">
                {meta.title}
              </span>
            </figcaption>
            <a
              href={FEATURED_VIDEO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent("featured_video_external_click", {
                  location: "home_media_section",
                  video_id: FEATURED_VIDEO_ID,
                  destination: "youtube",
                })
              }
              className="mt-3 inline-flex items-center gap-2 text-[12px] font-medium text-muted-foreground transition-colors hover:text-accent"
            >
              <Youtube className="h-3.5 w-3.5" />
              Watch on YouTube
            </a>
          </figure>

        </motion.div>
      </div>
    </section>
  );
}
